import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { CompanyBuildingView, OpportunityBuildingView } from '@api-proxies';
import { CompanyBuildingService } from '@http/clients/company-building.service';
import { OpportunityBuildingService } from '@http/clients/opportunity-building.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import { IconSanitizerService, ModalComponentBase, SnackbarNotificationService } from 'ngx-lib';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';
import { LocationDetailsComponent } from '../../location-details/location-details.component';

export type ModalEditAddressResult = CompanyBuildingView | OpportunityBuildingView;

export interface IModalEditAddressParameters {
    entityType?: EntityTypes.Company | EntityTypes.Opportunity;
    companyBuilding?: CompanyBuildingView;
    opportunityBuilding?: OpportunityBuildingView;
}

interface ILocationDetails {
    line2?: string;
    line1?: string;
    countryName?: string;
    stateName?: string;
    cityName?: string;
    zipCode?: string;
}

@Component({
    selector: 'app-modal-edit-address',
    templateUrl: './modal-edit-address.component.html',
    styleUrls: ['./modal-edit-address.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [...CDK_IMPORTS, LocationDetailsComponent, LoadingButtonComponent]
})
export class ModalEditAddressComponent
    extends ModalComponentBase<IModalEditAddressParameters, ModalEditAddressResult>
    implements OnInit
{
    private readonly companyBuildingService = inject(CompanyBuildingService);
    private readonly opportunityBuildingService = inject(OpportunityBuildingService);
    private readonly awaiter = inject(AwaiterService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly iconSanitizer = inject(IconSanitizerService);

    readonly isLoading = signal(false);
    readonly buildingName = signal('');
    readonly location = signal<ILocationDetails>({});
    readonly closeIcon: SafeHtml = this.iconSanitizer.getIcon('closeModal');

    private readonly entityType = signal<EntityTypes.Company | EntityTypes.Opportunity>(EntityTypes.Company);

    constructor() {
        super();
    }

    ngOnInit(): void {
        let entityType = this.parameters?.entityType;

        if (!entityType) {
            if (this.parameters?.opportunityBuilding) {
                entityType = EntityTypes.Opportunity;
            } else {
                entityType = EntityTypes.Company;
            }
        }

        this.entityType.set(entityType);

        let building: CompanyBuildingView | OpportunityBuildingView | undefined;

        if (entityType === EntityTypes.Opportunity) {
            building = this.parameters?.opportunityBuilding;
        } else {
            building = this.parameters?.companyBuilding;
        }

        this.buildingName.set(building?.buildingName ?? '');
        this.location.set({
            line1: building?.line1 ?? '',
            line2: building?.line2 ?? '',
            countryName: building?.countryName ?? '',
            stateName: building?.stateName ?? '',
            cityName: building?.cityName ?? '',
            zipCode: building?.zipCode ?? ''
        });
    }

    updateLine2(line2?: string): void {
        this.location.update((current) => ({
            ...current,
            line2
        }));
    }

    async saveAddress(): Promise<void> {
        await this.awaiter.awaitAction(
            'Saving Address Line 2...',
            async () => {
                const line2 = this.location().line2;
                let buildingSaved;
                if (this.entityType() === EntityTypes.Opportunity) {
                    buildingSaved = await this.opportunityBuildingService.save({
                        ...this.parameters?.opportunityBuilding,
                        line2
                    } as OpportunityBuildingView);
                } else if (this.entityType() === EntityTypes.Company) {
                    buildingSaved = await this.companyBuildingService.save({
                        ...this.parameters?.companyBuilding,
                        line2
                    } as CompanyBuildingView);
                }

                if (buildingSaved) {
                    this.snackbarNotificationService.success('Address Line 2 updated successfully.');
                    this.close(buildingSaved);
                } else {
                    this.snackbarNotificationService.error('An error occurred while trying to update the address.');
                }
            },
            (loading) => this.isLoading.set(loading)
        );
    }
}
